'use client';

import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  Select,
  Stack,
  Table,
  TableBody,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ZeroEtlIdentityColumnField,
  ZeroEtlRoutingMode,
} from '@repo/api-gw-sdk';
import { Roboto_Mono } from 'next/font/google';
import { useState } from 'react';

import { MainDialog } from '@/components/dialogs/mainDialog';
import { BorderlessTableCell } from '@/components/table/components';

import {
  FULLY_QUALIFIED_NAMESPACE,
  FULLY_QUALIFIED_TABLE,
  type ZeroEtlPolicyEditState,
} from './ZeroEtlPolicyCreationFlow';

const robotoMono = Roboto_Mono({
  weight: '600',
  subsets: ['latin'],
});

const ROUTING_MODE_LABELS: Record<ZeroEtlRoutingMode, string> = {
  [ZeroEtlRoutingMode.ZERO_ETL_ROUTING_MODE_FULLY_QUALIFIED]: 'Fully Qualified',
  [ZeroEtlRoutingMode.ZERO_ETL_ROUTING_MODE_MANUAL]: 'Manual',
};

const IDENTITY_COLUMN_OPTIONS = Object.values(ZeroEtlIdentityColumnField);

const IDENTITY_COLUMN_VALUES = new Set<string>(IDENTITY_COLUMN_OPTIONS);

function isIdentityColumnField(
  value: string
): value is ZeroEtlIdentityColumnField {
  return IDENTITY_COLUMN_VALUES.has(value);
}

function fieldToLabel(field: ZeroEtlIdentityColumnField): string {
  return field.toLowerCase();
}

function fieldToColumnName(field: ZeroEtlIdentityColumnField): string {
  return `_${field.toLowerCase()}`;
}

const TEMPLATE_VARIABLES = [
  '{source_cloud}',
  '{source_account_id}',
  '{source_region}',
  '{resource_id}',
  '{database_name}',
  '{schema_name}',
  '{table_name}',
];

interface RoutingPolicyPanelProps {
  state: ZeroEtlPolicyEditState;
  onChange: (updates: Partial<ZeroEtlPolicyEditState>) => void;
}

export const RoutingPolicyPanel = ({
  state,
  onChange,
}: RoutingPolicyPanelProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const isManual =
    state.routingMode === ZeroEtlRoutingMode.ZERO_ETL_ROUTING_MODE_MANUAL;

  const selectedFields = new Set(state.identityColumns.map((ic) => ic.field));

  const toggleField = (field: ZeroEtlIdentityColumnField) => {
    const updated = selectedFields.has(field)
      ? state.identityColumns.filter((ic) => ic.field !== field)
      : [
          ...state.identityColumns,
          { field, columnName: fieldToColumnName(field), partition: false },
        ];
    onChange({ identityColumns: updated });
  };

  const togglePartition = (field: ZeroEtlIdentityColumnField) => {
    onChange({
      identityColumns: state.identityColumns.map((ic) =>
        ic.field === field ? { ...ic, partition: !ic.partition } : ic
      ),
    });
  };

  return (
    <Stack gap='32px' width='100%' minWidth={0}>
      <Typography variant='h3'>Routing policy</Typography>
      <Stack gap='24px' overflow='auto'>
        {/* Routing Mode Selector */}
        <Stack gap='8px'>
          <Typography variant='body2' color='text.secondary'>
            Routing mode
          </Typography>
          <Button
            sx={{
              padding: '5px 12px',
              justifyContent: 'flex-start',
              width: 'fit-content',
            }}
            onClick={() => setDialogOpen(true)}
            disableFocusRipple
          >
            <Typography variant='subtitle2'>
              {ROUTING_MODE_LABELS[state.routingMode]}
            </Typography>
            <i className='material-symbols-arrow-drop-down-rounded text-[22px] ml-[4px]' />
          </Button>
        </Stack>

        <Divider />

        {/* Routing Target */}
        <Stack gap='16px'>
          {isManual ? (
            <>
              <Stack gap='8px'>
                <Typography variant='body2' color='text.secondary'>
                  Namespace
                </Typography>
                <TextField
                  value={state.namespaceTemplate}
                  onChange={(e) =>
                    onChange({ namespaceTemplate: e.target.value })
                  }
                  placeholder='{source_cloud}.{database_name}.{schema_name}'
                  size='small'
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontFamily: 'monospace',
                      fontSize: '13px',
                    },
                  }}
                />
              </Stack>
              <Stack gap='8px'>
                <Typography variant='body2' color='text.secondary'>
                  Table
                </Typography>
                <TextField
                  value={state.tableTemplate}
                  onChange={(e) => onChange({ tableTemplate: e.target.value })}
                  placeholder='{table_name}'
                  size='small'
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontFamily: 'monospace',
                      fontSize: '13px',
                    },
                  }}
                />
              </Stack>
              <Typography variant='body2' color='text.secondary'>
                Available variables: {TEMPLATE_VARIABLES.join(', ')}
              </Typography>
            </>
          ) : (
            <>
              <ReadOnlyField
                label='Namespace'
                value={FULLY_QUALIFIED_NAMESPACE}
              />
              <ReadOnlyField label='Table' value={FULLY_QUALIFIED_TABLE} />
            </>
          )}
        </Stack>

        <Divider />

        {/* Identity Columns — pinned width to namespace string */}
        <Stack gap='12px'>
          <Typography variant='body1' fontWeight='bold'>
            Identity columns
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Optionally add columns containing source identity information.
          </Typography>
          <Select
            value=''
            displayEmpty
            size='small'
            onChange={(e) => {
              const value = e.target.value;
              if (isIdentityColumnField(value) && !selectedFields.has(value)) {
                toggleField(value);
              }
            }}
            renderValue={() => (
              <Typography variant='body2' color='text.secondary'>
                Add a field...
              </Typography>
            )}
            sx={{
              fontFamily: 'monospace',
              fontSize: '13px',
            }}
          >
            {IDENTITY_COLUMN_OPTIONS.filter((f) => !selectedFields.has(f)).map(
              (field) => (
                <MenuItem key={field} value={field}>
                  {fieldToLabel(field)}
                </MenuItem>
              )
            )}
          </Select>
          {state.identityColumns.length > 0 && (
            <Table sx={{ tableLayout: 'fixed' }}>
              <TableBody>
                <TableRow sx={{ height: '32px' }}>
                  <BorderlessTableCell sx={{ width: '40px' }} />
                  <BorderlessTableCell sx={{ width: '80px' }}>
                    <Typography variant='body2' color='text.secondary'>
                      Partition
                    </Typography>
                  </BorderlessTableCell>
                  <BorderlessTableCell>
                    <Typography variant='body2' color='text.secondary'>
                      Column
                    </Typography>
                  </BorderlessTableCell>
                </TableRow>
                {state.identityColumns.map((ic) => (
                  <TableRow key={ic.field} sx={{ height: '40px' }}>
                    <BorderlessTableCell sx={{ width: '40px' }}>
                      <IconButton
                        size='small'
                        onClick={() => toggleField(ic.field)}
                      >
                        <i className='material-symbols-close-rounded text-sm' />
                      </IconButton>
                    </BorderlessTableCell>
                    <BorderlessTableCell sx={{ width: '80px' }}>
                      <Checkbox
                        checked={!!ic.partition}
                        onChange={() => togglePartition(ic.field)}
                        size='small'
                        sx={{ padding: '2px' }}
                      />
                    </BorderlessTableCell>
                    <BorderlessTableCell>
                      <Typography variant='body2'>
                        {fieldToLabel(ic.field)}
                      </Typography>
                    </BorderlessTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Stack>
      </Stack>

      <ChangeRoutingModeDialog
        open={dialogOpen}
        mode={state.routingMode}
        onCancel={() => setDialogOpen(false)}
        onSelect={(mode) => {
          setDialogOpen(false);
          onChange({ routingMode: mode });
        }}
      />
    </Stack>
  );
};

const ReadOnlyField = ({ label, value }: { label: string; value: string }) => (
  <Stack gap='4px'>
    <Typography variant='body2' color='text.secondary'>
      {label}
    </Typography>
    <Typography
      className={robotoMono.className}
      variant='body1'
      sx={{ wordBreak: 'break-word' }}
    >
      {value}
    </Typography>
  </Stack>
);

const ChangeRoutingModeDialog = ({
  open,
  mode,
  onCancel,
  onSelect,
}: {
  open: boolean;
  mode: ZeroEtlRoutingMode;
  onCancel: () => void;
  onSelect: (mode: ZeroEtlRoutingMode) => void;
}) => {
  const [modeValue, setModeValue] = useState(mode);

  const handleCancel = () => {
    setModeValue(mode);
    onCancel();
  };

  return (
    <MainDialog
      sx={{ minWidth: '720px', overflowX: 'hidden' }}
      isOpen={open}
      isLoading={false}
      header={{
        title: 'Routing mode',
        onClose: handleCancel,
      }}
      footer={{
        primaryButton: {
          'data-testid': 'approve-change-routing-mode',
          text: 'Select',
          onClick: () =>
            mode === modeValue ? handleCancel() : onSelect(modeValue),
        },
        secondaryButton: {
          'data-testid': 'cancel-change-routing-mode',
          onClick: handleCancel,
          text: 'Cancel',
        },
      }}
    >
      <Stack gap='40px' className='my-[20px]'>
        <Stack direction='row' className='mx-[40px]' gap='60px'>
          <Stack gap='16px' flex={1}>
            <FormControlLabel
              control={<Radio className='mr-[12px]' />}
              label='Fully qualified'
              checked={
                modeValue ===
                ZeroEtlRoutingMode.ZERO_ETL_ROUTING_MODE_FULLY_QUALIFIED
              }
              onChange={() =>
                setModeValue(
                  ZeroEtlRoutingMode.ZERO_ETL_ROUTING_MODE_FULLY_QUALIFIED
                )
              }
            />
            <Typography className='ml-[48px] w-[200px]' variant='body2'>
              Tables are written using a deterministic path derived from the
              source resource.
            </Typography>
          </Stack>
          <Divider orientation='vertical' flexItem />
          <Stack gap='8px' flex={1}>
            <ReadOnlyField
              label='Namespace'
              value={FULLY_QUALIFIED_NAMESPACE}
            />
            <ReadOnlyField label='Table' value={FULLY_QUALIFIED_TABLE} />
          </Stack>
        </Stack>
        <Divider />
        <Stack direction='row' className='mx-[40px]' gap='60px'>
          <Stack gap='16px' flex={1}>
            <FormControlLabel
              control={<Radio className='mr-[12px]' />}
              label='Manual'
              checked={
                modeValue === ZeroEtlRoutingMode.ZERO_ETL_ROUTING_MODE_MANUAL
              }
              onChange={() =>
                setModeValue(ZeroEtlRoutingMode.ZERO_ETL_ROUTING_MODE_MANUAL)
              }
            />
            <Typography className='ml-[48px] w-[200px]' variant='body2'>
              Provide custom namespace and table patterns using template
              variables.
            </Typography>
          </Stack>
          <Divider orientation='vertical' flexItem />
          <Stack gap='4px' flex={1}>
            <Typography variant='body2' color='text.secondary'>
              Available variables
            </Typography>
            {TEMPLATE_VARIABLES.map((v) => (
              <Typography
                key={v}
                variant='body2'
                sx={{ fontFamily: 'monospace' }}
              >
                {v}
              </Typography>
            ))}
          </Stack>
        </Stack>
      </Stack>
    </MainDialog>
  );
};
